import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminSlaPolicy } from '@central/contracts';
import { Button } from '@/components/Button';
import {
  alterarPoliticaSla,
  apagarPoliticaSla,
  chavesDeAdministracao,
  criarPoliticaSla,
  listarPoliticasSla,
} from '@/features/admin/admin.api';
import {
  AcoesDaLinha,
  Cabecalho,
  Campo,
  CascaDeCatalogo,
  Celula,
  Chave,
  chaveSugerida,
  CLASSE_DE_CAMPO,
  ConfirmacaoDeCatalogo,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  useEscritaDeCatalogo,
  type Intencao,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';

const TIPOS: Array<[AdminSlaPolicy['kind'], string]> = [
  ['SLA', 'SLA — compromisso com o cliente'],
  ['OLA', 'OLA — acordo interno entre equipes'],
  ['UC', 'UC — contrato com fornecedor'],
];

const METRICAS: Array<[AdminSlaPolicy['metric'], string]> = [
  ['FIRST_RESPONSE', 'Primeira resposta'],
  ['RESOLUTION', 'Resolução'],
  ['UPDATE_INTERVAL', 'Intervalo de atualização'],
];

const ROTULO_TIPO = new Map(TIPOS);
const ROTULO_METRICA = new Map(METRICAS);

function formatarMinutos(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

export function SlaPage() {
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.politicasSla,
    queryFn: listarPoliticasSla,
  });
  const politicas = consulta.data?.items ?? [];

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<AdminSlaPolicy | null>(null);
  const [confirmando, setConfirmando] = useState<{
    registro: AdminSlaPolicy;
    intencao: Intencao;
  } | null>(null);

  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarPoliticaSla(id, { active, version }),
    () => setConfirmando(null),
  );
  const remocao = useEscritaDeCatalogo(
    (id: string) => apagarPoliticaSla(id),
    () => setConfirmando(null),
  );

  const ocupado = atividade.mutacao.isPending || remocao.mutacao.isPending;

  return (
    <>
      <CascaDeCatalogo
        titulo="Políticas de SLA"
        resumo="As metas de tempo de atendimento. Cada política vale para uma métrica e um tipo de compromisso."
        rotuloDoBotao="Nova política"
        aoCriar={() => setCriando(true)}
        consulta={consulta}
        vazio="Nenhuma política cadastrada. Sem política, o SLA não é calculado para nenhum chamado."
      >
        <LinhaDeCabecalho>
          <Cabecalho>Política</Cabecalho>
          <Cabecalho>Chave</Cabecalho>
          <Cabecalho>Tipo</Cabecalho>
          <Cabecalho>Métrica</Cabecalho>
          <Cabecalho>Meta</Cabecalho>
          <Cabecalho>Aviso</Cabecalho>
          <Cabecalho>Contratual</Cabecalho>
          <Cabecalho>Estado</Cabecalho>
          <Cabecalho className="text-right">Ações</Cabecalho>
        </LinhaDeCabecalho>

        <tbody>
          {politicas.map((pol) => (
            <Linha key={pol.id} inativo={!pol.active}>
              <Celula className="font-medium">{pol.name}</Celula>
              <Celula>
                <Chave valor={pol.key} />
              </Celula>
              <Celula className="text-xs">{ROTULO_TIPO.get(pol.kind) ?? pol.kind}</Celula>
              <Celula className="text-xs">
                {ROTULO_METRICA.get(pol.metric) ?? pol.metric}
              </Celula>
              <Celula className="text-xs font-mono tabular-nums">
                {formatarMinutos(pol.targetMinutes)}
              </Celula>
              <Celula className="text-xs text-muted-foreground">
                {Math.round(pol.warnThreshold * 100)}%
              </Celula>
              <Celula className="text-xs">
                {pol.contractual ? 'Sim' : 'Não'}
              </Celula>
              <Celula>
                <EtiquetaDeAtividade ativo={pol.active} />
              </Celula>
              <Celula>
                <AcoesDaLinha
                  registro={{ id: pol.id, key: pol.key, name: pol.name, active: pol.active, version: pol.version, usage: { tickets: 0, children: 0 } }}
                  entidade="política"
                  ocupado={ocupado}
                  aoEditar={() => setEditando(pol)}
                  aoAlternarAtividade={() => {
                    if (!pol.active) {
                      atividade.mutacao.mutate({ id: pol.id, active: true, version: pol.version });
                      return;
                    }
                    setConfirmando({ registro: pol, intencao: 'desativar' });
                  }}
                  aoRemover={() => setConfirmando({ registro: pol, intencao: 'remover' })}
                />
              </Celula>
            </Linha>
          ))}
        </tbody>
      </CascaDeCatalogo>

      <DialogoDePolitica
        aberto={criando || editando !== null}
        politica={editando}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      <ConfirmacaoDeCatalogo
        aberto={confirmando !== null}
        intencao={confirmando?.intencao ?? 'desativar'}
        entidade="política de SLA"
        registro={
          confirmando
            ? { id: confirmando.registro.id, key: confirmando.registro.key, name: confirmando.registro.name, active: confirmando.registro.active, version: confirmando.registro.version, usage: { tickets: 0, children: 0 } }
            : null
        }
        erro={atividade.erro ?? remocao.erro}
        ocupado={ocupado}
        aoFechar={() => setConfirmando(null)}
        aoConfirmar={() => {
          if (!confirmando) return;
          if (confirmando.intencao === 'remover') {
            remocao.mutacao.mutate(confirmando.registro.id);
            return;
          }
          atividade.mutacao.mutate({
            id: confirmando.registro.id,
            active: false,
            version: confirmando.registro.version,
          });
        }}
      />
    </>
  );
}

function DialogoDePolitica({
  aberto,
  politica,
  aoFechar,
}: {
  aberto: boolean;
  politica: AdminSlaPolicy | null;
  aoFechar: () => void;
}) {
  const editando = politica !== null;

  const [nome, setNome] = useState('');
  const [chave, setChave] = useState('');
  const [chaveTocada, setChaveTocada] = useState(false);
  const [kind, setKind] = useState<AdminSlaPolicy['kind']>('SLA');
  const [metric, setMetric] = useState<AdminSlaPolicy['metric']>('FIRST_RESPONSE');
  const [targetHoras, setTargetHoras] = useState('');
  const [targetMinutos, setTargetMinutos] = useState('');
  const [warnPct, setWarnPct] = useState('80');
  const [contractual, setContractual] = useState(true);
  const [semeado, setSemeado] = useState<string | null>(null);

  const identidade = politica?.id ?? (aberto ? 'nova' : null);
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setNome(politica?.name ?? '');
    setChave(politica?.key ?? '');
    setChaveTocada(politica !== null);
    setKind(politica?.kind ?? 'SLA');
    setMetric(politica?.metric ?? 'FIRST_RESPONSE');
    if (politica) {
      const h = Math.floor(politica.targetMinutes / 60);
      const m = politica.targetMinutes % 60;
      setTargetHoras(h > 0 ? String(h) : '');
      setTargetMinutos(m > 0 ? String(m) : '');
    } else {
      setTargetHoras('');
      setTargetMinutos('');
    }
    setWarnPct(politica ? String(Math.round(politica.warnThreshold * 100)) : '80');
    setContractual(politica?.contractual ?? true);
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCatalogo(async () => {
    const totalMinutos = (parseInt(targetHoras || '0') * 60) + parseInt(targetMinutos || '0');
    if (totalMinutos < 1) throw new Error('A meta deve ser de pelo menos 1 minuto.');
    const warn = parseFloat(warnPct) / 100;

    if (politica) {
      return alterarPoliticaSla(politica.id, {
        name: nome.trim(),
        targetMinutes: totalMinutos,
        warnThreshold: warn,
        contractual,
        version: politica.version,
      });
    }
    return criarPoliticaSla({
      key: chave.trim(),
      name: nome.trim(),
      kind,
      metric,
      targetMinutes: totalMinutos,
      warnThreshold: warn,
      contractual,
    });
  }, aoFechar);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar "${politica.name}"` : 'Nova política de SLA'}
      descricao={
        editando
          ? 'O tipo e a métrica não mudam. Crie uma nova política se precisar de outra combinação.'
          : 'A chave e a combinação tipo + métrica são definitivas.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo id="sla-nome" rotulo="Nome">
          <input
            id="sla-nome"
            value={nome}
            onChange={(e) => {
              setNome(e.target.value);
              if (!chaveTocada) setChave(chaveSugerida(e.target.value));
            }}
            required
            minLength={2}
            maxLength={100}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            id="sla-chave"
            rotulo="Chave"
            dica={editando ? 'Imutável depois de criada.' : 'Minúsculas, dígitos e hífen.'}
          >
            <input
              id="sla-chave"
              value={chave}
              onChange={(e) => {
                setChaveTocada(true);
                setChave(e.target.value);
              }}
              disabled={editando}
              required
              pattern="[a-z0-9][a-z0-9.\-]*[a-z0-9]"
              minLength={2}
              maxLength={50}
              className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
            />
          </Campo>

          <Campo id="sla-tipo" rotulo="Tipo">
            <select
              id="sla-tipo"
              value={kind}
              disabled={editando}
              onChange={(e) => setKind(e.target.value as AdminSlaPolicy['kind'])}
              className={CLASSE_DE_CAMPO}
            >
              {TIPOS.map(([val, rot]) => (
                <option key={val} value={val}>{rot}</option>
              ))}
            </select>
          </Campo>
        </div>

        <Campo id="sla-metrica" rotulo="Métrica">
          <select
            id="sla-metrica"
            value={metric}
            disabled={editando}
            onChange={(e) => setMetric(e.target.value as AdminSlaPolicy['metric'])}
            className={CLASSE_DE_CAMPO}
          >
            {METRICAS.map(([val, rot]) => (
              <option key={val} value={val}>{rot}</option>
            ))}
          </select>
        </Campo>

        <fieldset className="space-y-1">
          <legend className="text-sm font-medium text-foreground">Meta de tempo</legend>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <input
                id="sla-horas"
                type="number"
                min={0}
                max={999}
                value={targetHoras}
                onChange={(e) => setTargetHoras(e.target.value)}
                placeholder="0"
                className={`${CLASSE_DE_CAMPO} w-20 text-right`}
              />
              <span className="text-sm text-muted-foreground">h</span>
            </div>
            <div className="flex items-center gap-1">
              <input
                id="sla-minutos"
                type="number"
                min={0}
                max={59}
                value={targetMinutos}
                onChange={(e) => setTargetMinutos(e.target.value)}
                placeholder="0"
                className={`${CLASSE_DE_CAMPO} w-20 text-right`}
              />
              <span className="text-sm text-muted-foreground">min</span>
            </div>
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            id="sla-aviso"
            rotulo="Aviso em (%)"
            dica="Percentual do tempo usado que acende o alerta."
          >
            <input
              id="sla-aviso"
              type="number"
              min={1}
              max={99}
              value={warnPct}
              onChange={(e) => setWarnPct(e.target.value)}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>

          <Campo id="sla-contratual" rotulo="Contratual" dica="Aparece no relatório de SLA do cliente.">
            <select
              id="sla-contratual"
              value={contractual ? 'sim' : 'nao'}
              onChange={(e) => setContractual(e.target.value === 'sim')}
              className={CLASSE_DE_CAMPO}
            >
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </Campo>
        </div>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            {editando ? 'Salvar' : 'Criar política'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
